import { makeObservable, observable, computed, action } from 'mobx';
import { TranscriptEditorStore } from '@src/transcript/TranscriptEditorStore';
import { TextElement, PunctElement } from '@src/transcript/models/TranscriptElement';
import { AdjustElementStartCommand } from '@src/transcript/commands/AdjustElementStartCommand';
import { AdjustElementEndCommand } from '@src/transcript/commands/AdjustElementEndCommand';
import { BatchCommand } from '@src/transcript/commands/BatchCommand';
import WaveformData from 'waveform-data';

// Constants
const AUTO_SAVE_DELAY = 500; // ms to wait before auto-saving changes

/**
 * Interface for elements with additional timeline-specific properties
 */
export interface TimelineElement {
  elementId: string;
  segmentId: string;
  paragraphId: string;
  element: TextElement | PunctElement;
  isEditable: boolean;
  originalStartTime?: number;
  originalEndTime?: number;
  adjustedStartTime?: number;
  adjustedEndTime?: number;
}

/**
 * Interface for silence gaps between elements
 */
export interface TimelineSilence {
  startTime: number;
  endTime: number;
  duration: number;
}

/**
 * Store for managing mini timeline state and operations
 */
export class MiniTimelineStore {
  // Core state
  editorStore: TranscriptEditorStore;
  elements: TimelineElement[] = [];
  silences: TimelineSilence[] = [];
  waveformData: WaveformData | null = null;
  
  // Playback state
  currentTime: number = 0;
  isPlaying: boolean = false;
  
  // Timeline view state
  visibleTimeRange: [number, number] = [0, 0];
  totalTimeRange: [number, number] = [0, 0];
  zoomLevel: number = 1;
  
  // Command tracking
  pendingChanges: Map<string, { startTime?: number, endTime?: number }> = new Map();
  
  // Auto-save timer
  private autoSaveTimer: number | null = null;
  private isAutoSaving: boolean = false;
  
  /**
   * Creates a new MiniTimelineStore
   * 
   * @param editorStore Reference to the main editor store
   */
  constructor(editorStore: TranscriptEditorStore) {
    this.editorStore = editorStore;

    // Use makeObservable with explicit annotations for better compatibility
    makeObservable(this, {
      // State properties
      elements: observable,
      silences: observable,
      waveformData: observable,
      currentTime: observable,
      isPlaying: observable,
      visibleTimeRange: observable,
      totalTimeRange: observable,
      zoomLevel: observable,
      pendingChanges: observable,

      // Computed properties
      pendingChangesCount: computed,

      // Actions
      setWaveformData: action,
      setCurrentTime: action,
      setIsPlaying: action,
      setVisibleTimeRange: action,
      zoomIn: action,
      zoomOut: action,
      fitToTimeline: action,
      fitToSelection: action,
      panTimeline: action,
      adjustElementStart: action,
      adjustElementEnd: action,
      resetElement: action,
      resetAllElements: action,
      reset: action
    });
  }
  
  /**
   * Gets the number of pending changes
   */
  get pendingChangesCount(): number {
    return this.pendingChanges.size;
  }
  
  /**
   * Load elements for fine alignment
   * 
   * @param selectedElementIds Selected element IDs to align
   */
  async loadElements(selectedElementIds: Array<{
    elementId: string;
    segmentId: string;
    paragraphId: string;
  }>): Promise<void> {
    if (!this.editorStore.container) {
      throw new Error('Transcript container not available');
    }
    
    // Get selected elements
    const selectedElements: TimelineElement[] = [];
    const container = this.editorStore.container;
    
    for (const { elementId, segmentId, paragraphId } of selectedElementIds) {
      const segment = container.getSegment(segmentId);
      if (!segment) continue;
      
      const paragraph = segment.getParagraph(paragraphId);
      if (!paragraph) continue;
      
      const element = paragraph.getElement(elementId);
      if (!element) continue;
      
      selectedElements.push({
        elementId,
        segmentId,
        paragraphId,
        element,
        isEditable: true,
        originalStartTime: element.type === 'text' ? (element as TextElement).startTime : undefined,
        originalEndTime: element.type === 'text' ? (element as TextElement).endTime : undefined,
        adjustedStartTime: element.type === 'text' ? (element as TextElement).startTime : undefined,
        adjustedEndTime: element.type === 'text' ? (element as TextElement).endTime : undefined
      });
    }
    
    if (selectedElements.length === 0) {
      throw new Error('No valid elements found for alignment');
    }
    
    // Find time range of selected elements
    let minTime = Number.MAX_VALUE;
    let maxTime = 0;
    
    selectedElements.forEach(item => {
      if (item.element.type === 'text') {
        const textElement = item.element as TextElement;
        if (textElement.startTime !== undefined && textElement.startTime < minTime) {
          minTime = textElement.startTime;
        }
        if (textElement.endTime !== undefined && textElement.endTime > maxTime) {
          maxTime = textElement.endTime;
        }
      }
    });
    
    // Default to 0-10 if no valid times found
    if (minTime === Number.MAX_VALUE) minTime = 0;
    if (maxTime === 0) maxTime = 10;
    
    // Add buffer of 5 seconds on each side
    const startBuffer = Math.max(0, minTime - 5);
    const endBuffer = maxTime + 5;
    
    // Get all elements within the expanded time range
    const allElements: TimelineElement[] = [];
    
    // Track segments and paragraphs we've already processed
    const processedSegments = new Set<string>();
    const processedParagraphs = new Set<string>();
    
    // Function to get a unique key for a paragraph
    const getParagraphKey = (segId: string, paraId: string) => `${segId}:${paraId}`;
    
    // Process selected elements first
    selectedElements.forEach(item => {
      allElements.push(item);
      
      // Mark this paragraph as processed
      processedParagraphs.add(getParagraphKey(item.segmentId, item.paragraphId));
    });
    
    // Get all segments that overlap with our time range
    container.segments.forEach(segment => {
      // Skip if already processed
      if (processedSegments.has(segment.id)) return;
      
      // Check if segment overlaps with our time range
      if (segment.endTime < startBuffer || segment.startTime > endBuffer) return;
      
      // Process paragraphs
      segment.paragraphs.forEach(paragraph => {
        // Skip if already processed
        if (processedParagraphs.has(getParagraphKey(segment.id, paragraph.id))) return;
        
        // Check if paragraph overlaps with our time range
        if (paragraph.endTime < startBuffer || paragraph.startTime > endBuffer) return;
        
        // Add all elements in this paragraph
        paragraph.elements.forEach(element => {
          // Check if it's already in our selected elements
          const isAlreadySelected = selectedElements.some(
            sel => sel.elementId === element.id && 
                   sel.segmentId === segment.id && 
                   sel.paragraphId === paragraph.id
          );
          
          if (!isAlreadySelected) {
            allElements.push({
              elementId: element.id,
              segmentId: segment.id,
              paragraphId: paragraph.id,
              element,
              isEditable: false, // Context elements are not editable
              originalStartTime: element.type === 'text' ? (element as TextElement).startTime : undefined,
              originalEndTime: element.type === 'text' ? (element as TextElement).endTime : undefined,
              adjustedStartTime: element.type === 'text' ? (element as TextElement).startTime : undefined,
              adjustedEndTime: element.type === 'text' ? (element as TextElement).endTime : undefined
            });
          }
        });
        
        // Mark paragraph as processed
        processedParagraphs.add(getParagraphKey(segment.id, paragraph.id));
      });
      
      // Mark segment as processed
      processedSegments.add(segment.id);
    });
    
    // Sort all elements by start time
    allElements.sort((a, b) => {
      const aStart = a.originalStartTime || 0;
      const bStart = b.originalStartTime || 0;
      return aStart - bStart;
    });
    
    // Find silence gaps
    const silences: TimelineSilence[] = [];
    let previousEndTime = startBuffer;
    
    // Only consider text elements for silence detection
    const textElements = allElements.filter(
      item => item.element.type === 'text' && item.originalStartTime !== undefined
    );
    
    textElements.forEach((item, index) => {
      const startTime = item.originalStartTime || 0;
      
      // If there's a gap > 0.5s, add a silence
      if (startTime - previousEndTime > 0.5) {
        silences.push({
          startTime: previousEndTime,
          endTime: startTime,
          duration: startTime - previousEndTime
        });
      }
      
      // Update previous end time
      if (item.originalEndTime !== undefined) {
        previousEndTime = item.originalEndTime;
      }
      
      // Special case for last element
      if (index === textElements.length - 1 && item.originalEndTime !== undefined) {
        // If there's a gap at the end > 0.5s, add a silence
        if (endBuffer - item.originalEndTime > 0.5) {
          silences.push({
            startTime: item.originalEndTime,
            endTime: endBuffer,
            duration: endBuffer - item.originalEndTime
          });
        }
      }
    });
    
    // Update store state
    this.elements = allElements;
    this.silences = silences;
    this.totalTimeRange = [startBuffer, endBuffer];
    this.visibleTimeRange = [startBuffer, endBuffer];
    this.currentTime = startBuffer;
    this.pendingChanges.clear();
  }
  
  /**
   * Sets the waveform data
   */
  setWaveformData(data: WaveformData | null): void {
    this.waveformData = data;
  }
  
  /**
   * Sets the current playback time
   */
  setCurrentTime(time: number): void {
    this.currentTime = Math.max(
      this.totalTimeRange[0],
      Math.min(time, this.totalTimeRange[1])
    );
  }
  
  /**
   * Sets the playback state
   */
  setIsPlaying(isPlaying: boolean): void {
    this.isPlaying = isPlaying;
  }
  
  /**
   * Sets the visible time range with zoom level
   */
  setVisibleTimeRange(start: number, end: number): void {
    // Ensure valid range
    start = Math.max(this.totalTimeRange[0], start);
    end = Math.min(this.totalTimeRange[1], end);
    
    // Ensure minimum range of 1 second
    if (end - start < 1) {
      end = start + 1;
    }
    
    this.visibleTimeRange = [start, end];
    
    // Calculate zoom level (1 = full view, > 1 = zoomed in)
    const totalRange = this.totalTimeRange[1] - this.totalTimeRange[0];
    const visibleRange = end - start;
    this.zoomLevel = totalRange / visibleRange;
  }
  
  /**
   * Zoom in by a factor
   */
  zoomIn(factor: number = 1.5): void {
    const currentRange = this.visibleTimeRange[1] - this.visibleTimeRange[0];
    const newRange = currentRange / factor;
    
    // Center zoom around current time if possible
    const centerTime = this.currentTime;
    const newStart = Math.max(
      this.totalTimeRange[0],
      centerTime - newRange / 2
    );
    const newEnd = Math.min(
      this.totalTimeRange[1],
      newStart + newRange
    );
    
    this.setVisibleTimeRange(newStart, newEnd);
  }
  
  /**
   * Zoom out by a factor
   */
  zoomOut(factor: number = 1.5): void {
    const currentRange = this.visibleTimeRange[1] - this.visibleTimeRange[0];
    const newRange = Math.min(
      this.totalTimeRange[1] - this.totalTimeRange[0],
      currentRange * factor
    );
    
    // Center zoom around current time if possible
    const centerTime = this.currentTime;
    const newStart = Math.max(
      this.totalTimeRange[0],
      centerTime - newRange / 2
    );
    const newEnd = Math.min(
      this.totalTimeRange[1],
      newStart + newRange
    );
    
    this.setVisibleTimeRange(newStart, newEnd);
  }
  
  /**
   * Fit the visible range to the entire timeline
   */
  fitToTimeline(): void {
    this.setVisibleTimeRange(this.totalTimeRange[0], this.totalTimeRange[1]);
  }
  
  /**
   * Fit the visible range to the selected elements
   */
  fitToSelection(): void {
    // Find time range of selected elements
    let minTime = Number.MAX_VALUE;
    let maxTime = 0;
    
    const selectedElements = this.elements.filter(item => item.isEditable);
    
    selectedElements.forEach(item => {
      if (item.element.type === 'text') {
        const startTime = item.adjustedStartTime ?? item.originalStartTime;
        const endTime = item.adjustedEndTime ?? item.originalEndTime;
        
        if (startTime !== undefined && startTime < minTime) {
          minTime = startTime;
        }
        if (endTime !== undefined && endTime > maxTime) {
          maxTime = endTime;
        }
      }
    });
    
    // Add a small buffer
    const buffer = Math.max(1, (maxTime - minTime) * 0.1);
    
    // Set visible range
    this.setVisibleTimeRange(
      Math.max(this.totalTimeRange[0], minTime - buffer),
      Math.min(this.totalTimeRange[1], maxTime + buffer)
    );
  }
  
  /**
   * Pan the timeline by a time offset
   */
  panTimeline(offset: number): void {
    const currentRange = this.visibleTimeRange[1] - this.visibleTimeRange[0];
    let newStart = this.visibleTimeRange[0] + offset;
    
    // Ensure within bounds
    newStart = Math.max(this.totalTimeRange[0], newStart);
    newStart = Math.min(this.totalTimeRange[1] - currentRange, newStart);
    
    this.setVisibleTimeRange(newStart, newStart + currentRange);
  }
  
  /**
   * Trigger auto-save with debounce
   * This ensures we don't send too many save requests in rapid succession
   */
  private triggerAutoSave(): void {
    // Don't trigger if already saving
    if (this.isAutoSaving) return;
    
    // Clear existing timer if any
    if (this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    
    // Only auto-save if there are changes
    if (this.pendingChangesCount > 0) {
      
      // Set new timer
      this.autoSaveTimer = window.setTimeout(async () => {
        try {
          // Set saving flag
          this.isAutoSaving = true;
          
          // Apply the changes to the editor
          const commands = this.generateCommands();
          console.log('commands', commands)
          if (commands.length > 0) {
            const batchCommand = new BatchCommand(
              this.editorStore.editor,
              commands
            );
            
            const success = await this.editorStore.editor.commandManager.execute(batchCommand);
            if (success) {              
              // Update original times to reflect saved changes
              for (const [key, changes] of this.pendingChanges.entries()) {
                const [segmentId, paragraphId, elementId] = key.split(':');
                const element = this.elements.find(
                  item => item.elementId === elementId && 
                         item.segmentId === segmentId && 
                         item.paragraphId === paragraphId
                );
                
                if (element) {
                  // Update original values to match new saved values
                  if (changes.startTime !== undefined) {
                    element.originalStartTime = changes.startTime;
                  }
                  if (changes.endTime !== undefined) {
                    element.originalEndTime = changes.endTime;
                  }
                }
              }
              
              // Clear pending changes since they've been saved
              this.pendingChanges.clear();
            } else {
              console.error('Failed to apply timing adjustments');
            }
          }
        } catch (err) {
          console.error('Error during auto-save:', err);
        } finally {
          // Reset saving flag
          this.isAutoSaving = false;
          this.autoSaveTimer = null;
        }
      }, AUTO_SAVE_DELAY);
    }
  }

  /**
   * Adjust element start time
   */
  adjustElementStart(
    elementId: string, 
    segmentId: string, 
    paragraphId: string, 
    newStartTime: number
  ): void {
    // Find the element
    const element = this.elements.find(
      item => item.elementId === elementId && 
             item.segmentId === segmentId && 
             item.paragraphId === paragraphId
    );
    
    if (!element || !element.isEditable || element.element.type !== 'text') {
      console.error('Cannot adjust start time: element not found or not editable');
      return;
    }
    
    // Ensure valid start time
    const endTime = element.adjustedEndTime ?? element.originalEndTime;
    if (endTime !== undefined && newStartTime >= endTime) {
      console.error('Start time must be before end time');
      return;
    }
    
    // Update the element's adjusted start time
    element.adjustedStartTime = newStartTime;
    
    // Track the change
    const key = `${segmentId}:${paragraphId}:${elementId}`;
    this.pendingChanges.set(key, {
      ...this.pendingChanges.get(key),
      startTime: newStartTime
    });
    
    // Trigger auto-save
    this.triggerAutoSave();
  }
  
  /**
   * Adjust element end time
   */
  adjustElementEnd(
    elementId: string, 
    segmentId: string, 
    paragraphId: string, 
    newEndTime: number
  ): void {
    // Find the element
    const element = this.elements.find(
      item => item.elementId === elementId && 
             item.segmentId === segmentId && 
             item.paragraphId === paragraphId
    );
    
    if (!element || !element.isEditable || element.element.type !== 'text') {
      console.error('Cannot adjust end time: element not found or not editable');
      return;
    }
    
    // Ensure valid end time
    const startTime = element.adjustedStartTime ?? element.originalStartTime;
    if (startTime !== undefined && newEndTime <= startTime) {
      console.error('End time must be after start time');
      return;
    }
    
    // Update the element's adjusted end time
    element.adjustedEndTime = newEndTime;
    
    // Track the change
    const key = `${segmentId}:${paragraphId}:${elementId}`;
    this.pendingChanges.set(key, {
      ...this.pendingChanges.get(key),
      endTime: newEndTime
    });
    
    // Trigger auto-save
    this.triggerAutoSave();
  }
  
  /**
   * Reset an element to its original timing
   */
  resetElement(
    elementId: string, 
    segmentId: string, 
    paragraphId: string
  ): void {
    // Find the element
    const element = this.elements.find(
      item => item.elementId === elementId && 
             item.segmentId === segmentId && 
             item.paragraphId === paragraphId
    );
    
    if (!element || !element.isEditable) {
      console.error('Cannot reset element: not found or not editable');
      return;
    }
    
    // Reset to original values
    element.adjustedStartTime = element.originalStartTime;
    element.adjustedEndTime = element.originalEndTime;
    
    // Remove from pending changes
    const key = `${segmentId}:${paragraphId}:${elementId}`;
    this.pendingChanges.delete(key);
    
    // Cancel any pending auto-save if there are no more changes
    if (this.pendingChangesCount === 0 && this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }
  
  /**
   * Reset all elements to their original timing
   */
  resetAllElements(): void {
    this.elements.forEach(element => {
      if (element.isEditable) {
        element.adjustedStartTime = element.originalStartTime;
        element.adjustedEndTime = element.originalEndTime;
      }
    });
    
    this.pendingChanges.clear();
    
    // Cancel any pending auto-save
    if (this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }
  
  /**
   * Generate commands for the current adjustments
   */
  generateCommands(): Array<AdjustElementStartCommand | AdjustElementEndCommand> {
    const commands: Array<AdjustElementStartCommand | AdjustElementEndCommand> = [];
    
    // Process all pending changes
    for (const [key, changes] of this.pendingChanges.entries()) {
      const [segmentId, paragraphId, elementId] = key.split(':');
      
      // Find the element in our data
      const element = this.elements.find(
        item => item.elementId === elementId && 
               item.segmentId === segmentId && 
               item.paragraphId === paragraphId
      );
      
      if (!element || !element.isEditable || element.element.type !== 'text') {
        continue;
      }
      
      // Create commands
      if (changes.startTime !== undefined && 
          changes.startTime !== element.originalStartTime) {
        commands.push(new AdjustElementStartCommand(
          this.editorStore.editor,
          segmentId,
          paragraphId,
          elementId,
          element.originalStartTime ?? 0,
          changes.startTime
        ));
      }
      
      if (changes.endTime !== undefined && 
          changes.endTime !== element.originalEndTime) {
        commands.push(new AdjustElementEndCommand(
          this.editorStore.editor,
          segmentId,
          paragraphId,
          elementId,
          element.originalEndTime ?? 0,
          changes.endTime
        ));
      }
    }
    
    return commands;
  }
  
  /**
   * Reset the store to initial state
   */
  reset(): void {
    // Cancel any pending auto-save
    if (this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    
    this.isAutoSaving = false;
    this.elements = [];
    this.silences = [];
    this.waveformData = null;
    this.currentTime = 0;
    this.isPlaying = false;
    this.visibleTimeRange = [0, 0];
    this.totalTimeRange = [0, 0];
    this.zoomLevel = 1;
    this.pendingChanges.clear();
  }
}